import type { PricelistChannelMarkupsQuery } from "@/generated/graphql";
import {
	Button,
	Can,
	DialogHeader,
	DialogTitle,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	toast,
	useStackDialog,
} from "@k3-tech/react-kit";
import { EditIcon } from "lucide-react";
import { usePricelistChannelMarkups } from "../usecases/usePricelistChannelMarkups";
import PricelistSalesChannelUpsertDialog from "./PricelistSalesChannelUpsertDialog";

type ChannelMarkup = NonNullable<
	PricelistChannelMarkupsQuery["pricelist"]
>["channelMarkups"][number];

const isOwnMarkup = (row: ChannelMarkup) => row.markupSource === "pricelist";

export default function PricelistSalesChannelMarkupCard({
	pricelistID,
	onSaved,
}: {
	pricelistID?: string;
	onSaved?: () => void;
}) {
	const { data, fetching, load } = usePricelistChannelMarkups(pricelistID);
	const { createDialog, closeDialog } = useStackDialog();

	const rows = data?.pricelist?.channelMarkups ?? [];

	const openUpsertDialog = (row: ChannelMarkup) => {
		if (!pricelistID) return;
		createDialog({
			template: (
				<>
					<DialogHeader>
						<DialogTitle>Channel Price Markup</DialogTitle>
					</DialogHeader>
					<PricelistSalesChannelUpsertDialog
						pricelistID={pricelistID}
						salesChannelID={row.salesChannelID}
						channelName={row.salesChannel.salesChannelName}
						priceMarkupPerc={isOwnMarkup(row) ? row.markupPerc : undefined}
						pricelistSalesChannelID={
							row.pricelistSalesChannel?.pricelistSalesChannelID
						}
						onCancel={() => closeDialog()}
						onSuccess={(message) => {
							load();
							closeDialog();
							onSaved?.();
							toast.success(message);
						}}
					/>
				</>
			),
		});
	};

	return (
		<div className="overflow-hidden rounded-md border border-border">
			<Table>
				<TableHeader className="bg-primary">
					<TableRow className="hover:bg-primary">
						<TableHead className="text-white">Channel</TableHead>
						<TableHead className="w-48 text-white">Markup (%)</TableHead>
						<TableHead className="w-28 text-white">Actions</TableHead>
					</TableRow>
				</TableHeader>

				<TableBody>
					{fetching && rows.length === 0 ? (
						<TableRow>
							<TableCell colSpan={3}>
								<Skeleton className="h-8" />
							</TableCell>
						</TableRow>
					) : rows.length === 0 ? (
						<TableRow>
							<TableCell colSpan={3}>
								<div className="text-center text-muted-foreground">
									{pricelistID
										? "No active marketplace channel"
										: "Save the pricelist first to set channel markups"}
								</div>
							</TableCell>
						</TableRow>
					) : (
						rows.map((row) => (
							<TableRow key={row.salesChannelID}>
								<TableCell className="font-medium">
									{row.salesChannel.salesChannelName}
								</TableCell>

								{/* A channel without its own markup follows the channel default, which is not shown here. */}
								<TableCell>{isOwnMarkup(row) ? row.markupPerc : "-"}</TableCell>

								<TableCell>
									<Can permissions="pricelist.edit">
										<Button
											size="icon"
											variant="default"
											onClick={() => openUpsertDialog(row)}
										>
											<EditIcon size={16} />
										</Button>
									</Can>
								</TableCell>
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</div>
	);
}
